/**
 * Normalizes variable placeholder markup inside rendered rich text: merges placeholders a wrapper was split into
 * (keeping the most informed one), drops legacy icon/text wrappers, and syncs the icon classes with data-icon-state.
 * Runs on a live editor's DOM and on static renders, so both show variables the same way.
 */
export function normalizeVariablePlaceholderDom(root: HTMLElement): void {
  const hasSplitPlaceholders = !!root.querySelector('.variable-wrapper .variable-placeholder + .variable-placeholder')

  if (hasSplitPlaceholders) {
    const wrappers = root.querySelectorAll<HTMLElement>('.variable-wrapper')

    const scorePlaceholder = (placeholder: HTMLElement): number => {
      const iconState = placeholder.getAttribute('data-icon-state')
      const hasDefault = placeholder.getAttribute('data-has-default') === 'true'
      const hasRecipient = placeholder.getAttribute('data-has-recipient') === 'true'
      let score = 0
      if (iconState === 'done') score += 4
      if (hasDefault) score += 3
      if (iconState === 'pend') score += 2
      if (hasRecipient) score += 1
      if (placeholder.getAttribute('data-variable-name')) score += 1
      return score
    }

    const pickBestPlaceholder = (placeholders: HTMLElement[]): HTMLElement => {
      let best = placeholders[0]
      let bestScore = scorePlaceholder(best)
      for (let i = 1; i < placeholders.length; i++) {
        const candidate = placeholders[i]
        const score = scorePlaceholder(candidate)
        if (score > bestScore) {
          best = candidate
          bestScore = score
        }
      }
      return best
    }

    const normalizeWrapper = (wrapper: HTMLElement): void => {
      const placeholders = Array.from(wrapper.querySelectorAll<HTMLElement>('.variable-placeholder'))
      if (placeholders.length <= 1) return

      const preferred =
        placeholders.find(p => p.getAttribute('data-variable-name')) ||
        placeholders.find(p => /\{\{\s*([^}]+?)\s*\}\}/.test((p.textContent || '').trim())) ||
        placeholders[0]
      const best = pickBestPlaceholder(placeholders) || preferred

      let variableName = (best.getAttribute('data-variable-name') || '').trim()
      if (!variableName) {
        const match = (best.textContent || '').match(/\{\{\s*([^}]+?)\s*\}\}/)
        variableName = match?.[1]?.trim() || ''
      }
      if (!variableName) {
        const combined = placeholders.map(p => p.textContent || '').join('')
        const match = combined.match(/\{\{\s*([^}]+?)\s*\}\}/)
        variableName = match?.[1]?.trim() || ''
      }

      placeholders.forEach(p => {
        if (p !== best) p.remove()
      })

      // Remove stray brace-only text nodes inside wrapper
      Array.from(wrapper.childNodes).forEach(node => {
        if (node.nodeType !== Node.TEXT_NODE) return
        const text = (node.textContent || '').trim()
        if (!text || text === '{' || text === '}' || text === '{{' || text === '}}') {
          node.remove()
        }
      })

      if (variableName) {
        best.textContent = `{{ ${variableName} }}`
        if (!best.getAttribute('data-variable-name')) {
          best.setAttribute('data-variable-name', variableName)
        }
      }
    }

    wrappers.forEach(wrapper => normalizeWrapper(wrapper))
  }

  const placeholders = root.querySelectorAll<HTMLElement>('.variable-placeholder')

  placeholders.forEach(placeholder => {
    // Clean up any legacy icon/text wrappers injected previously
    placeholder.querySelector('.icon')?.remove()
    const textSpan = placeholder.querySelector<HTMLElement>('.text')
    if (textSpan) {
      while (textSpan.firstChild) {
        placeholder.insertBefore(textSpan.firstChild, textSpan)
      }
      textSpan.remove()
    }

    const iconState = placeholder.getAttribute('data-icon-state')
    placeholder.classList.toggle('icon-warn', iconState === 'warn')
    placeholder.classList.toggle('icon-pend', iconState === 'pend')
    placeholder.classList.toggle('icon-done', iconState === 'done')
  })
}
